import { put, takeEvery, select } from 'redux-saga/effects';

import { SELECT_DEALER } from 'constants/ActionTypes';
import { STATUS } from 'constants/ChatConstants';
import { PATH } from 'constants/RoutesConstants';
import { ENTITY_TYPE } from 'modules/app/constants';
import { updateMessageFromSocket } from 'modules/chat/actions/details';
import { RECEIVE_MESSAGE_FROM_SOCKET } from 'modules/chat/actionTypes';
import { getConversationSocketParameters, getMessageById } from 'modules/chat/selectors/details';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { actionTypeFormatter } from 'modules/sockets/actionTypes';
import { SOCKET_ACTION, SOCKET_TOPIC } from 'modules/sockets/constants';
import { subscribeOnPage } from 'modules/sockets/utils.middleware';
import { createNotification } from 'modules/toast-notifications/actions';
import { getCurrentUserId } from 'modules/users/selectors/currentUser';

const TOPICS = [SOCKET_TOPIC.CHAT, SOCKET_TOPIC.DETAILS];
const MESSAGE_ACTIONS = [SOCKET_ACTION.MESSAGE_SENT, SOCKET_ACTION.MESSAGE_NOT_SENT, SOCKET_ACTION.MESSAGE_SEEN];
const MESSAGE_RECEIVED_ACTIONS = TOPICS.flatMap(topic =>
  MESSAGE_ACTIONS.map(action => actionTypeFormatter(topic, ENTITY_TYPE.MESSAGE, action)));

const MESSAGE_REACTION_ACTIONS = [SOCKET_ACTION.MESSAGE_REACTION_REACT, SOCKET_ACTION.MESSAGE_REACTION_UNREACT];
const MESSAGE_REACT_ACTIONS = TOPICS.flatMap(topic =>
  MESSAGE_REACTION_ACTIONS.map(action => actionTypeFormatter(topic, ENTITY_TYPE.MESSAGE_REACTION, action)));

const SOCKET_UPDATE_MESSAGE_ACTIONS = [...MESSAGE_RECEIVED_ACTIONS, ...MESSAGE_REACT_ACTIONS];

function* onReceiveSocketMessage({ payload }) {
  const { message, myClients } = payload?.data ?? {};
  if (message) {
    const storedMsg = yield select(state => getMessageById(state, message.id));
    yield put(updateMessageFromSocket(message, myClients, !storedMsg));
  }
}

/** @param {import("../types").UpdateMessageFromSocketAction} action */
function* onMessageAppended({ payload: { isNew, message } }) {
  const currentUserId = yield select(getCurrentUserId);
  const isFromOtherUser = message?.createdBy?.id !== currentUserId;

  if (!isNew && !isFromOtherUser && message.status === STATUS.NOT_SENT) {
    yield put(
      createNotification({
        type: 'error',
        message: { id: 'page.chat.notification.error', defaultMessage: 'Your message could not be delivered' },
      })
    );
  }

  if (isNew && isFromOtherUser) {
    yield put(
      createNotification({
        type: 'success',
        message: { id: 'page.chat.notification.received', defaultMessage: 'You have a new message!' },
      })
    );
  }
}

const channelPattern = ({ type, payload }) => type === LOCATION_CHANGE && payload.action !== 'REPLACE';

export default function* chatSocketSagas() {
  yield subscribeOnPage(
    [PATH.CHAT],
    SOCKET_TOPIC.CHAT,
    getConversationSocketParameters,
    [SELECT_DEALER],
    channelPattern
  );
  yield takeEvery(SOCKET_UPDATE_MESSAGE_ACTIONS, onReceiveSocketMessage);
  yield takeEvery(RECEIVE_MESSAGE_FROM_SOCKET, onMessageAppended);
}
